import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useTheme, getColors } from "../ThemeContext";
import { useAIAdvice } from "../useAIAdvice";
import { useWell } from "../WellContext";
import {
  Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, ComposedChart, Cell, Label, BarChart,
} from "recharts";
import { API } from "../config/api";
import PlotSummary from "../components/PlotSummary";

const C = {
  bg:"#050b14",panel:"#080f1e",card:"#0b1525",border:"#0f2040",border2:"#162a4a",
  cyan:"#00d4ff",teal:"#00ffcc",amber:"#ffb84d",pink:"#ff4da6",purple:"#a855f7",
  blue:"#3b9eff",red:"#ff3d5a",green:"#00ff88",orange:"#ff7c2a",lime:"#b0ff4a",
  text:"#e2eaf8",muted:"#4a6080",muted2:"#2a3f60",
};

function applyTheme(colors) { Object.assign(C, colors); }

const MONO = "'Space Mono', monospace";

const SANS = "'Syne', sans-serif";

const FIELD_META = {
  ArrivalVelocity: {
    label: "Plunger Arrival Velocity", unit: "ft/min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v < 350)   return { color: C.red,   text: `TOO SLOW (${v.toFixed(0)} ft/min), below safe min 350. Extend shut-in.` };
      if (v > 1200)  return { color: C.amber, text: `TOO FAST (${v.toFixed(0)} ft/min), above 1200. Reduce casing pressure target.` };
      return           { color: C.green, text: `SAFE (${v.toFixed(0)} ft/min), optimal plunger travel range.` };
    },
    advice: v => v < 350 ? "▽ Too slow (<350 ft/min), plunger may drop back. Extend shut-in time or reduce line pressure." : v > 1200 ? "▶ Too fast (>1200 ft/min), excess energy. Reduce shut-in time to save gas." : "◆ Optimal range (350, 1200 ft/min). Perfect lift!",
  },
  FlowRateAtClose: {
    label: "Gas Production Rate (at valve close)", unit: "MCF/day",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v < 50)   return { color: C.red,   text: `LOW (${v.toFixed(1)} MCF/day), well under-producing. Check liquid loading.` };
      return          { color: C.teal,  text: `${v.toFixed(1)} MCF/day, gas sold at valve close. Higher = more revenue.` };
    },
    advice: v => v < 50 ? "▲ Low flow rate, well may be under-performing. Check valve, plunger, separator." : v > 500 ? "◆ Excellent production rate!" : "◆ Normal flow rate.",
  },
  CsgAtOpen: {
    label: "Casing Pressure at Valve Open", unit: "psi",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v < 300)   return { color: C.red,   text: `LOW (${v.toFixed(0)} psi), not enough reservoir drive to push plunger up.` };
      return           { color: C.cyan,  text: `${v.toFixed(0)} psi, reservoir pressure driving plunger upward.` };
    },
    advice: v => v < 300 ? "▲ Low, may not have enough gas to lift plunger. Increase shut-in time." : v > 800 ? "◆ High, good reservoir pressure. Consider opening valve sooner." : "◆ Normal range. Monitor trends.",
  },
  LoadFactor: {
    label: "Load Factor", unit: "",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v >= 0.5)  return { color: C.red,   text: `OVERLOADED (${v.toFixed(3)}), plunger WILL NOT arrive. Extend shut-in NOW.` };
      if (v >= 0.35) return { color: C.amber, text: `APPROACHING LIMIT (${v.toFixed(3)}), close to 0.5 threshold, monitor.` };
      return           { color: C.green, text: `SAFE (${v.toFixed(3)}), plunger can lift this liquid column.` };
    },
    advice: v => v >= 0.5 ? "■ Load factor ≥0.5, plunger cannot lift liquid. Extend shut-in significantly before opening." : v >= 0.35 ? "▲ Load factor approaching 0.5, add 10, 15 min to shut-in time as precaution." : "◆ Load factor safe. Plunger can lift current liquid column.",
  },
  TimeOffTotal: {
    label: "Shut-In Duration", unit: "min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      return { color: C.teal, text: `${v.toFixed(0)} min shut-in, valve closed, casing pressure rebuilding.` };
    },
    advice: v => v < 30 ? "▲ Short shut-in, casing may not build enough pressure. Increase to 45, 90 min." : v > 300 ? "▲ Very long shut-in, wasting production time. Reduce if casing pressure is sufficient." : "◆ Shut-in time is in good range.",
  },
  TimeAfterFlow: {
    label: "Afterflow Duration", unit: "min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      return { color: C.teal, text: `${v.toFixed(0)} min afterflow, this is your gas production window.` };
    },
    advice: v => v < 10 ? "▲ Short after-flow, closing valve too early. Extend to capture more gas." : v > 120 ? "▲ Very long after-flow, may delay next cycle." : "◆ Good after-flow window.",
  },
  score: {
    label: "Cycle Health Score", unit: "/100",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No score" };
      if (v >= 75)  return { color: C.green, text: `EXCELLENT (${v}), all plunger physics operating within spec.` };
      if (v >= 55)  return { color: C.green, text: `GOOD (${v}), minor deviations, within acceptable range.` };
      if (v >= 35)  return { color: C.amber, text: `WARNING (${v}), multiple parameters out of spec. Review cycle.` };
      return          { color: C.red,   text: `CRITICAL (${v}), serious issues. Check velocity, load factor, arrival.` };
    },
    advice: v => v >= 75 ? "◆ Excellent cycle score, well operating within all physics limits." : v >= 55 ? "◆ Good score, minor deviations. Monitor trend for deterioration." : v >= 35 ? "▲ Warning, multiple parameters out of spec. Review velocity and load factor." : "■ Critical score, serious issues detected. Check plunger arrival and casing pressure.",
  },
  anomaly_score: {
    label: "Anomaly Score", unit: "/10",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No score" };
      if (v < 3.33) return { color: C.green, text: `GOOD (${v.toFixed(1)}), cycle within expected operating range.` };
      if (v < 6.66) return { color: C.amber, text: `WARNING (${v.toFixed(1)}), some parameters deviated from normal.` };
      return          { color: C.red,   text: `CRITICAL (${v.toFixed(1)}),  unusual cycle, investigate triggers below.` };
    },
    advice: v => v < 3.33 ? "◆ Good, all parameters within expected statistical range. No action needed." : v < 6.66 ? "▲ Warning, one or more parameters deviated. Check triggered features and compare to prior cycles." : "■ Critical, very unusual cycle. Investigate triggered parameters immediately.",
  },
  // FIXED
  violations: {
    label: "Physics Violations", unit: "count",
    interpret: v => {
      if (v == null || v === 0) return { color: C.green, text: "0 violations, cycle passed all physics equations." };
      if (v === 1)              return { color: C.amber, text: `WARNING: 1 violation, one physics equation failed this cycle.` };
      return                             { color: C.red,   text: `CRITICAL: ${v} violations, multiple physics failures. Cycle is out of spec.` };
    },
    advice: v => (!v || v === 0) ? "◆ Zero violations, well operating within all physics limits this cycle." : v === 1 ? "▲ Warning, one physics violation. Check load factor and casing pressure at open." : `■ Critical, ${v} violations. Multiple physics failures. Review load factor, velocity, and shut-in timing.`,
  },
  actual: {
    label: "Actual Production", unit: "MCF/day",
    interpret: v => v != null ? { color: C.teal, text: `Actual measured: ${v.toFixed(2)} MCF/day` } : { color: C.muted, text: "No data" },
    advice: v => v != null ? (v < 50 ? "▲ Low production rate, check for liquid loading or valve issues." : "◆ Normal production rate for this cycle.") : null,
  },
  forecast: {
    label: "Deep Sequence Neural Network Forecast", unit: "MCF/day",
    interpret: v => v != null ? { color: C.cyan, text: `Model predicts ${v.toFixed(2)} MCF/day for this future cycle.` } : { color: C.muted, text: "No forecast" },
    advice: v => v != null ? `Model forecasts ${v.toFixed(1)} MCF/day. Compare to your historical average to assess trajectory.` : null,
  },
  upper_80: {
    label: "Upper 80% Confidence", unit: "MCF/day",
    interpret: v => v != null ? { color: C.cyan, text: `Optimistic bound: ${v.toFixed(2)} MCF/day (top 10% of outcomes).` } : { color: C.muted, text: "-" },
    advice: () => "Upper bound of the 80% confidence interval, actual production will exceed this only 10% of the time.",
  },
  lower_80: {
    label: "Lower 80% Confidence", unit: "MCF/day",
    interpret: v => v != null ? { color: C.blue, text: `Conservative bound: ${v.toFixed(2)} MCF/day (bottom 10% of outcomes).` } : { color: C.muted, text: "-" },
    advice: () => "Lower bound of the 80% confidence interval, actual production falls below this only 10% of the time.",
  },
  rolling_avg: {
    label: "Rolling Average Score", unit: "/100",
    interpret: v => v != null ? { color: C.cyan, text: `Smoothed trend: ${v.toFixed(1)}/100 over recent cycles.` } : { color: C.muted, text: "-" },
    advice: v => v != null ? (v < 55 ? "▲ Rolling average below 55, sustained performance issues. Review shut-in setpoints." : "◆ Rolling average healthy, no sustained degradation trend.") : null,
  },
  significance: {
    label: "Change Significance (σ)", unit: "σ",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "-" };
      if (v > 1.5)  return { color: C.red,   text: `CRITICAL High shift (${v.toFixed(2)}σ). This parameter changed significantly vs prior period.` };
      if (v > 0.8)  return { color: C.amber, text: `WARNING Moderate shift (${v.toFixed(2)}σ). Noticeable change, monitor closely.` };
      return          { color: C.green, text: `GOOD Low shift (${v.toFixed(2)}σ). Minor variation, within normal range.` };
    },
    advice: v => v > 1.5 ? "■ Critical, this parameter shifted substantially. Investigate whether it was a setpoint change or a well condition change." : v > 0.8 ? "▲ Warning, moderate shift detected. Monitor over next 5, 10 cycles to confirm trend." : "◆ Good, within normal cycle-to-cycle variation. No action needed.",
  },
  arr: {
    label: "Arrival Time", unit: "min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No arrival data" };
      if (v > 45) return { color: C.red,   text: `CRITICAL (${Number(v).toFixed(1)} min), very slow arrival.` };
      if (v > 25) return { color: C.amber, text: `WARNING (${Number(v).toFixed(1)} min), slower than preferred.` };
      return        { color: C.green, text: `GOOD (${Number(v).toFixed(1)} min), arrival in expected window.` };
    },
    advice: v => v == null ? null : v > 45 ? "■ Critical slow arrival, check liquid load and shut-in energy." : v > 25 ? "▲ Warning, arrival trending slow. Review build-up." : "◆ Good arrival time.",
  },
  csg: {
    label: "Casing Pressure", unit: "psi",
    interpret: v => v == null ? { color: C.muted, text: "No CP data" } : { color: C.cyan, text: `Casing pressure ${Number(v).toFixed(1)} psi.` },
    advice: () => "Review casing pressure vs recent baseline for step changes or drift.",
  },
  tbg: {
    label: "Tubing Pressure", unit: "psi",
    interpret: v => v == null ? { color: C.muted, text: "No TP data" } : { color: C.cyan, text: `Tubing pressure ${Number(v).toFixed(1)} psi.` },
    advice: () => "Compare tubing vs line pressure when diagnosing surface restriction.",
  },
  line: {
    label: "Line Pressure", unit: "psi",
    interpret: v => v == null ? { color: C.muted, text: "No line data" } : { color: C.muted, text: `Line pressure ${Number(v).toFixed(1)} psi.` },
    advice: () => "Line pressure is the surface reference for restriction and leakage checks.",
  },
  hit: {
    label: "Flagged Event", unit: "",
    interpret: v => v == null ? { color: C.green, text: "GOOD, no anomaly flag on this cycle." } : { color: C.red, text: `CRITICAL, anomaly marker on this cycle.` },
    advice: v => v == null ? "◆ Good, cycle not flagged." : "■ Critical flagged cycle, review evidence and recommended action.",
  },
  count: {
    label: "Cycle Count", unit: "cycles",
    interpret: v => v != null ? { color: C.muted, text: `${v} cycles in this operating range.` } : { color: C.muted, text: "-" },
    advice: v => v > 50 ? "◆ High frequency, this operating range is most common for well." : v < 5 ? "▲ Rare operating range, outlier conditions. Investigate what drives these cycles." : "◆ Normal distribution bin.",
  },
};

const GENERIC_KEYS = new Set([
  "count","value","bin","actual","lstm","target","step",
  "violations","score","anomaly_score","rolling_avg",
  "significance","forecast","upper_80","lower_80",
  "arr","csg","tbg","line","hit","diff","i",
]);

// ── Anomaly marker severity: yellow = minor · orange = warning · red = critical ──
const SEV_YELLOW  = "#facc15";
const SIGNAL_KEYS = new Set(["arr","csg","tbg","line","diff"]);
const sevLevelColor = lvl => lvl === "critical" ? C.red : lvl === "warning" ? C.orange : SEV_YELLOW;
const sevLevelLabel = lvl => lvl === "critical" ? "CRITICAL" : lvl === "warning" ? "WARNING" : "MINOR";
// marker series are named mk_<signal>_<level>, e.g. mk_tbg_critical
// Ground-truth status line sent to the AI advisor (keywords map to its ■ / ▲ symbols)
const sevStatusText = (lvl, typeLabel) =>
  lvl === "critical" ? `CRITICAL, SEVERE ${typeLabel} anomaly flagged on this cycle`
  : lvl === "warning" ? `WARNING, ${typeLabel} anomaly flagged on this cycle`
  : `MINOR (MILD), small ${typeLabel} deviation, approaching the alert limit`;
// highest anomaly level among the markers on this cycle ("" when none)
const cycleAnomalyLevel = raw => raw && raw._levels
  ? Object.values(raw._levels).reduce((a, l) => levelRank(l) > levelRank(a) ? l : a, "") : "";
// make the advice symbol agree with the marker colour: ■ critical · ▲ warning / minor
const forceAdviceSymbol = (text, lvl) => {
  if (!text || !lvl) return text;
  const want = lvl === "critical" ? "■" : "▲";
  const m = /^(◆|▲|■|▽|▶)\s/.exec(text);
  return m ? want + text.slice(1) : `${want} ${text}`;
};
const markerParse = k => {
  const m = /^mk_(arr|csg|tbg)_(critical|warning|minor)$/.exec(String(k || ""));
  return m ? { sig: m[1], level: m[2] } : null;
};

function SmartTooltip({ active, payload, label, note }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const prevKeyRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    function onMove(e) { setMousePos({ x: e.clientX, y: e.clientY }); }
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const raw      = active && payload?.length ? payload[0]?.payload || {} : {};
  const cycleNum = raw.CycleSerialNumber ?? raw.cycle ?? raw.index ?? null;
  const timeStr  = raw.time || raw.timestamp || raw.date || null;
  const fieldKey = raw._actualFieldKey || null;

  let primaryField = null;
  let primaryValue = null;
  let primaryUnit  = "";
  let primaryIsMarker = false;

  const allFields = [];

  if (active && payload?.length) {
    for (const p of payload) {
      const rawKey    = p.dataKey || p.name || "";
      // Prefer explicit chart field key when series name is generic
      let lookupKey = rawKey;
      if ((GENERIC_KEYS.has(rawKey) || !FIELD_META[rawKey]) && fieldKey && !SIGNAL_KEYS.has(rawKey)) {
        lookupKey = fieldKey;
      }
      const mk = markerParse(rawKey);
      if (mk) lookupKey = mk.sig;
      const meta      = FIELD_META[lookupKey] || FIELD_META[rawKey] || null;
      const val       = typeof p.value === "number" ? p.value : (p.value != null && !isNaN(Number(p.value)) ? Number(p.value) : null);

      if (val !== null && rawKey !== "i") {
        const rowLevel = mk ? mk.level : (raw._levels ? raw._levels[lookupKey] : null);
        const pointStatus = raw._levels ? "" : String(raw.status || "").toLowerCase();
        let interp = meta?.interpret?.(val);
        const anomLevel = cycleAnomalyLevel(raw);
        const anomType  = String(raw.anomaly_type || "anomaly").replace(/_/g, " ");
        if (rowLevel) {
          interp = { color: sevLevelColor(rowLevel), text: sevStatusText(rowLevel, anomType) };
        } else if (pointStatus === "critical" || rawKey === "hitCritical") {
          interp = { color: C.red, text: "CRITICAL, flagged anomaly on this cycle." };
        } else if (pointStatus === "warning" || rawKey === "hitWarning") {
          interp = { color: C.amber, text: "WARNING, flagged anomaly on this cycle." };
        } else if (pointStatus === "good" && (raw.hit != null || rawKey === "hitGood")) {
          interp = { color: C.green, text: "GOOD, info-level marker on this cycle." };
        }
        allFields.push({
          field: lookupKey || rawKey,
          label: meta?.label || p.name || rawKey,
          value: Math.round(val * 100) / 100,
          unit:  meta?.unit || "",
          // on a flagged cycle only the anomaly severity is sent as ground truth, so
          // threshold-based GOOD/WARNING text from other rows cannot downgrade a critical dot
          status: (anomLevel && !rowLevel) ? "" : (interp?.text || ""),
        });
        // Always set a primary field so AI advice is requested (even without FIELD_META)
        if (primaryField === null || (mk && !primaryIsMarker)) {
          primaryIsMarker = !!mk;
          primaryField = lookupKey || rawKey;
          primaryValue = val;
          primaryUnit  = meta?.unit || "";
        }
      }
    }
  }

  const context = {};
  Object.entries(raw).forEach(([k, v]) => {
    if (typeof v === "number" && !k.startsWith("_") && !k.startsWith("mk_") && k !== "index") {
      context[k] = Math.round(v * 100) / 100;
    }
  });
  if (cycleNum != null) context.cycle_num = cycleNum;
  const adviceLevel = cycleAnomalyLevel(raw);
  if (adviceLevel) context.anomaly_severity_rank = levelRank(adviceLevel);
  if (raw._factors) context.factors = raw._factors;   // causes behind this flag → shown in the AI hover advice
  if (raw.anomaly_type) context.anomaly_type_code = Array.from(String(raw.anomaly_type)).reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) % 100003, 7);

  useEffect(() => {
    if (!active || !primaryField || primaryValue == null) return;
    const rounded  = typeof primaryValue === "number"
      ? Math.round(primaryValue * 10) / 10 : primaryValue;
    const allValsKey = allFields.map(f => `${f.field}:${f.value}:${f.status}`).join("|");
    const key = `${primaryField}_${rounded}_${allValsKey}`;
    if (prevKeyRef.current === key) return;
    prevKeyRef.current = key;
    fetchAdvice(primaryField, primaryValue, primaryUnit, cycleNum, context, allFields);
  }, [active, primaryField, primaryValue, JSON.stringify(allFields)]);

  useEffect(() => {
    if (!active) { clearAdvice(); }
  }, [active]);

  if (!active || !payload?.length) return null;
  const TIP_W = 360;
  const TIP_H = 320;
  const GAP   = 14;
  const vw    = window.innerWidth;
  const vh    = window.innerHeight;
  let left    = mousePos.x + GAP;
  let top     = mousePos.y + GAP;
  if (left + TIP_W > vw - 8) left = mousePos.x - TIP_W - GAP;
  if (top  + TIP_H > vh - 8) top  = mousePos.y - TIP_H - GAP;

  const cycleLabel = label != null
    ? (isNaN(Number(String(label))) ? String(label) : `Cycle ${label}`)
    : (cycleNum != null ? `Cycle ${cycleNum}` : null);

  const tooltipContent = (
    <div style={{
      position: "fixed", left, top,
      background: C.card, border: `1px solid ${C.border2}`, borderRadius: 12,
      padding: "12px 16px", fontFamily: MONO, fontSize: 11,
      boxShadow: "0 12px 40px #000e", maxWidth: TIP_W,
      zIndex: 99999, pointerEvents: "none",
    }}>
      {/* Header */}
      {(cycleLabel || timeStr || note) && (
        <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 7, marginBottom: 8 }}>
          {cycleLabel && <div style={{ color: C.cyan, fontWeight: 700, fontSize: 12, marginBottom: 2 }}>{cycleLabel}</div>}
          {timeStr    && <div style={{ color: C.muted, fontSize: 9 }}>{timeStr}</div>}
          {note       && <div style={{ color: C.muted, fontSize: 9, marginTop: 2 }}>{note}</div>}
        </div>
      )}

      {/* Series rows */}
      {payload.map((p, i) => {
        const rawKey    = p.dataKey || p.name || "";
        const isGeneric = GENERIC_KEYS.has(rawKey) && !SIGNAL_KEYS.has(rawKey);
        const mk        = markerParse(rawKey);
        const lookupKey = mk ? mk.sig : (isGeneric && fieldKey ? fieldKey : rawKey);
        const meta      = FIELD_META[lookupKey] || FIELD_META[rawKey];

        let displayName;
        if      (rawKey === "actual")        displayName = meta?.label ? `Actual, ${meta.label}` : "Actual";
        else if (rawKey === "lstm")          displayName = meta?.label ? `Deep Seq NN, ${meta.label}` : "LSTM";
        else if (rawKey === "target")        displayName = "Target";
        else if (rawKey === "rolling_avg")   displayName = "Rolling Average";
        else if (rawKey === "forecast")      displayName = "Deep Seq NN Forecast";
        else if (rawKey === "upper_80")      displayName = "Upper 80% CI";
        else if (rawKey === "lower_80")      displayName = "Lower 80% CI";
        else if (rawKey === "violations")    displayName = "Physics Violations";
        else if (rawKey === "anomaly_score") displayName = "Anomaly Score";
        else if (rawKey === "significance")  displayName = "Significance (σ)";
        else if (rawKey === "score")         displayName = meta?.label || "Health Score";
        else if (rawKey === "count")         displayName = meta?.label || "Cycle Count";
        else                                 displayName = mk ? `${meta?.label || p.name} · ${sevLevelLabel(mk.level)} marker` : (meta?.label || p.name || rawKey);
        let tooltipColor = p.color || C.cyan;
        if (rawKey === "lower_80") tooltipColor = C.blue;

        const isCountBar = rawKey === "count";
        const unit       = isCountBar ? "cycles" : (meta?.unit || "");
        const val        = p.value;
        const formatted  = typeof val === "number"
          ? (Math.abs(val) >= 100 ? val.toFixed(1) : val.toFixed(2))
          : String(val ?? "-");
        // Prefer plot severity from the data point when present so red markers
        // always show CRITICAL (not absolute threshold GOOD/WARNING).
        const rowLevel = mk ? mk.level : (raw._levels ? raw._levels[lookupKey] : null);
        const pointStatus = raw._levels ? "" : String(raw.status || "").toLowerCase();
        const isFlagHit = ["hit", "hitCritical", "hitWarning", "hitGood"].includes(rawKey)
          || raw.hit != null || raw.hitCritical != null || raw.hitWarning != null || raw.hitGood != null;
        let interp = meta?.interpret ? meta.interpret(val) : null;
        const typeLabel = (raw.anomaly_type || "anomaly").replace(/_/g, " ");
        const valTxt = typeof val === "number" ? (Math.abs(val)>=100?val.toFixed(1):val.toFixed(2)) : val;
        if (rowLevel) {
          const lvlTxt = rowLevel === "minor"
            ? `MINOR, low-severity ${typeLabel} marker on this cycle`
            : `${sevLevelLabel(rowLevel)}, flagged ${typeLabel} on this cycle`;
          interp = { color: sevLevelColor(rowLevel), text: `${lvlTxt} (${valTxt}${unit ? " " + unit : ""}).` };
          tooltipColor = sevLevelColor(rowLevel);
        } else if (pointStatus === "critical" || rawKey === "hitCritical") {
          interp = { color: C.red, text: `CRITICAL, flagged ${typeLabel} on this cycle (${valTxt}${unit ? " " + unit : ""}).` };
          tooltipColor = C.red;
        } else if (pointStatus === "warning" || rawKey === "hitWarning") {
          interp = { color: C.amber, text: `WARNING, flagged ${typeLabel} on this cycle (${valTxt}${unit ? " " + unit : ""}).` };
          tooltipColor = C.amber;
        } else if ((pointStatus === "good" || rawKey === "hitGood") && isFlagHit) {
          interp = { color: C.green, text: `GOOD, minor / info-level ${typeLabel} marker (${valTxt}${unit ? " " + unit : ""}).` };
          tooltipColor = C.green;
        } else if (interp?.color) {
          tooltipColor = interp.color;
        }

        return (
          <div key={i} style={{ marginBottom: i < payload.length - 1 ? 10 : 0 }}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", gap: 12,
              paddingBottom: interp ? 4 : 0,
            }}>
              <span style={{ color: C.muted, fontSize: 10, flex: 1, lineHeight: 1.3 }}>
                {displayName}
              </span>
              <span style={{ textAlign: "right", flexShrink: 0 }}>
                <span style={{ color: tooltipColor, fontWeight: 700, fontSize: 12 }}> 
                  {formatted}
                </span>
                {unit && (
                  <span style={{ color: C.muted, fontSize: 9, marginLeft: 3 }}>{unit}</span>
                )}
              </span>
            </div>
            {interp && (
              <div style={{
                marginTop: 3, padding: "4px 8px",
                background: `${interp.color}12`,
                border: `1px solid ${interp.color}33`,
                borderRadius: 6, fontSize: 10,
                color: interp.color, lineHeight: 1.5,
              }}>
                {interp.text}
              </div>
            )}
            {i < payload.length - 1 && (
              <div style={{ height: 1, background: C.border, marginTop: 8 }} />
            )}
          </div>
        );
      })}

      {/* AI Recommendation */}
      <div style={{
        marginTop: 10, borderTop: `1px solid ${C.border}`,
        fontSize: 10, lineHeight: 1.6,
        background: loading ? `${C.muted}08` : `${C.cyan}08`,
        borderRadius: 6, padding: "6px 8px",
      }}>
        {loading
          ? <span style={{ color: C.muted }}>Generating AI Recommendations…</span>
          : advice
            ? renderAdvice(forceAdviceSymbol(advice, adviceLevel))
            : <span style={{ color: C.muted }}>Hover a data point for AI advice</span>
        }
      </div>
    </div>
  );
  return ReactDOM.createPortal(tooltipContent, document.body);
}

function makeCycleFormatter() {
  return (val) => { const n=Number(val); return isNaN(n)?String(val):`C${n}`; };
}

function cycleInterval(len,maxLabels=10) {
  if(!len) return 0;
  return Math.max(0,Math.floor(len/maxLabels)-1);
}

/* ───────────── TIME-WINDOW HELPERS (same bar / axis / look as Overview, AI Controller, GLR) ───────────── */
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = n => String(n).padStart(2, "0");
const HOUR = 3600000, DAY = 86400000;

// backend sends "YYYY-MM-DD HH:MM" - parse as plain local clock time (no timezone shifting)
function parseT(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
}
const fmtDT   = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtD    = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`; };
const fmtHM   = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const toInput = ms => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

function fmtSpan(ms) {
  if (ms < HOUR)      return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 2 * DAY)   return `${Math.round(ms / HOUR)} h`;
  const d = Math.round(ms / DAY);
  return d >= 14 ? `${d} days (${(d / 7).toFixed(1)} wk)` : `${d} days`;
}
function fmtAgo(ms) {
  if (ms < HOUR)    return "just now";
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / DAY)} d ago`;
}

const PRESETS = [
  { id: "24h",   label: "24 H",   ms: DAY },
  { id: "7d",    label: "7 D",    ms: 7 * DAY },
  { id: "30d",   label: "30 D",   ms: 30 * DAY },
  { id: "90d",   label: "90 D",   ms: 90 * DAY },
  { id: "cyc30", label: "LAST 30 CYC", cycles: 30 },
  { id: "all",   label: "ALL" },
];
const MIN_POINTS = 3;   // fewer cycles than this cannot draw a meaningful trend

/* Apply a window to ANY array of rows that carry `_t` (ms). Presets are measured back from the LATEST cycle in the data, not from "now". */
function applyWin(w, tMax, arr) {
  if (w.mode === "all" || tMax == null) return arr;
  if (w.mode === "cyc30") {
    if (arr.some(r => r.well != null)) {            // multi-well rows -> newest 30 of EACH well
      const by = {};
      arr.forEach(r => { (by[r.well] = by[r.well] || []).push(r); });
      return Object.values(by).flatMap(a => a.slice(-30));
    }
    return arr.slice(-30);
  }
  if (w.mode === "custom") {
    const f = w.from ? parseT(`${w.from} 00:00`) : -Infinity;
    const t = w.to   ? parseT(`${w.to} 23:59`)   :  Infinity;
    return arr.filter(d => d._t != null && d._t >= f && d._t <= t);
  }
  const p = PRESETS.find(x => x.id === w.mode);
  return arr.filter(d => d._t != null && d._t >= tMax - p.ms);
}
/* `rows` = every cycle time the window bar should know about ({_t, well?}). `filter(arr)` windows any per-well / per-view array the same way. */
function useTimeWindow(rows) {
  const [win, setWin] = useState({ mode: "all", from: "", to: "" });
  const tMin = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.min(...v) : null; }, [rows]);
  const tMax = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.max(...v) : null; }, [rows]);
  const view = useMemo(() => applyWin(win, tMax, rows), [rows, win, tMax]);
  const presetCounts = useMemo(() => Object.fromEntries(PRESETS.map(p => [p.id, applyWin({ mode: p.id }, tMax, rows).length])), [rows, tMax]);
  return { win, setWin, view, presetCounts, tMin, tMax, isAll: win.mode === "all", filter: arr => applyWin(win, tMax, arr) };
}

/* Second row of the X axis = timestamp of the cycle sitting at that position. */
function DateTick({ x, y, payload, index, lookup, lastIdx, shortSpan }) {
  const t = lookup.get(payload.value);
  if (t == null) return null;
  const anchor = index === 0 ? "start" : index === lastIdx ? "end" : "middle";
  const l1 = shortSpan ? fmtHM(t) : fmtD(t);
  const l2 = shortSpan ? fmtD(t)  : fmtHM(t);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={anchor} fill={C.amber} fontSize={9} fontFamily={MONO} dy={11}>{l1}</text>
      <text textAnchor={anchor} fill={C.muted} fontSize={8} fontFamily={MONO} dy={22}>{l2}</text>
    </g>
  );
}

/* Call as a function (NOT <Component/>) - recharts only recognises <XAxis> as a direct child.
   Rows must carry `key` (default "index") and `_t`. labelText = the "Cycle Number →" caption, moved under the timestamp row. */
function dateXAxis(rows, key = "index", nTicks = 5, labelText, opts = {}) {
  const stamped = rows.filter(r => r._t != null);
  if (!stamped.length) return null;
  const last = stamped.length - 1;
  const idx = new Set();
  for (let i = 0; i < nTicks; i++) idx.add(Math.round((i * last) / Math.max(nTicks - 1, 1)));
  const ticks = [...idx].filter(i => stamped[i]).map(i => stamped[i][key]);
  const lookup = new Map(stamped.map(r => [r[key], r._t]));
  const span = stamped.length > 1 ? (stamped[last]._t - stamped[0]._t) : 0;
  return (
    <XAxis xAxisId="date" dataKey={key} ticks={ticks} interval={0} tickLine={false} axisLine={false} height={opts.height || 32}
      tick={<DateTick lookup={lookup} lastIdx={ticks.length - 1} shortSpan={span > 0 && span < 3 * DAY} />}
      label={labelText ? { value: labelText, position: "insideBottom", offset: opts.labelOffset ?? -14, fill: C.muted, fontSize: 9, fontFamily: MONO } : undefined} />
  );
}
const hasStamps = rows => rows.some(r => r._t != null);

function DataWindowBar({ ctl, total, note, isDark }) {
  const { win, setWin, view, presetCounts, tMin, tMax, isAll } = ctl;
  const vs      = view.filter(d => d._t != null);
  const vStart  = vs.length ? Math.min(...vs.map(d => d._t)) : null;
  const vEnd    = vs.length ? Math.max(...vs.map(d => d._t)) : null;
  const hasTime = vStart != null && vEnd != null;
  const spanMs  = hasTime ? Math.max(vEnd - vStart, 0) : 0;
  const tooFew  = view.length < MIN_POINTS;
  const latestAgo = tMax != null ? Date.now() - tMax : null;
  const btn = (active, disabled) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
    background: active ? `${C.amber}22` : "transparent", color: disabled ? C.muted2 : active ? C.amber : C.muted,
    border: `1px solid ${active ? C.amber : C.border2}`, opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  });
  const dateInput = { fontFamily: MONO, fontSize: 10, background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px", colorScheme: isDark ? "dark" : "light" };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "14px 18px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: C.muted }}>DATA WINDOW</span>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>
          {hasTime ? `${fmtDT(vStart)}  →  ${fmtDT(vEnd)}` : "no timestamps available"}
        </span>
        {hasTime && <span style={{ fontFamily: MONO, fontSize: 10, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "2px 8px" }}>
          {isAll ? "CUMULATIVE" : "WINDOW"} · {fmtSpan(spanMs)}
        </span>}
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 5, padding: "2px 8px" }}>
          {view.length} of {total} cycles
        </span>
        {latestAgo != null && (
          <span title="Presets are measured back from the newest cycle in the data, not from today's date"
            style={{ fontFamily: MONO, fontSize: 10, color: latestAgo > DAY ? C.orange : C.green, background: `${latestAgo > DAY ? C.orange : C.green}12`, border: `1px solid ${latestAgo > DAY ? C.orange : C.green}33`, borderRadius: 5, padding: "2px 8px" }}>
            Latest cycle {fmtDT(tMax)} · {fmtAgo(latestAgo)}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {PRESETS.map(p => {
          const n = presetCounts[p.id];
          const disabled = p.id !== "all" && n < MIN_POINTS;
          return (
            <button key={p.id} disabled={disabled} onClick={() => setWin({ mode: p.id, from: "", to: "" })}
              title={disabled ? `Only ${n} cycle${n === 1 ? "" : "s"} in this window, too few to plot a trend. Try a wider window.` : `${n} cycles`}
              style={btn(win.mode === p.id, disabled)}>
              {p.label} <span style={{ opacity: 0.7 }}>· {n}</span>
            </button>
          );
        })}
        <button onClick={() => setWin(w => ({ mode: "custom", from: w.from || (tMin != null ? toInput(tMin) : ""), to: w.to || (tMax != null ? toInput(tMax) : "") }))}
          style={btn(win.mode === "custom", false)}>CUSTOM RANGE</button>
        {win.mode === "custom" && (
          <>
            <input type="date" value={win.from} min={tMin != null ? toInput(tMin) : undefined} max={win.to || (tMax != null ? toInput(tMax) : undefined)}
              onChange={e => setWin(w => ({ ...w, from: e.target.value }))} style={dateInput} />
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>to</span>
            <input type="date" value={win.to} min={win.from || (tMin != null ? toInput(tMin) : undefined)} max={tMax != null ? toInput(tMax) : undefined}
              onChange={e => setWin(w => ({ ...w, to: e.target.value }))} style={dateInput} />
          </>
        )}
      </div>
      {tooFew && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.orange, marginTop: 8 }}>
          ⚠ Only {view.length} cycle{view.length === 1 ? "" : "s"} in this range, widen the window to see a trend.
        </div>
      )}
    </div>
  );
}


/* window the typed-anomaly report: series per well + events (events are kept when their cycle is inside the window) */
function windowTyped(data, stamped, tw) {
  const byWell = {};
  stamped.forEach(s => { (byWell[s.well_id] = byWell[s.well_id] || []).push(s); });
  const series = [], keep = {};
  Object.entries(byWell).forEach(([wid, rows]) => {
    rows.sort((a, b) => (a.i ?? 0) - (b.i ?? 0));
    const v = tw.filter(rows);
    keep[wid] = new Set(v.map(r => r.i));
    series.push(...v);
  });
  const events = data.events.filter(e => keep[e.well_id] && keep[e.well_id].has(e.cycle_index));
  const counts_by_type = {};
  Object.keys(data.counts_by_type).forEach(t => { counts_by_type[t] = events.filter(e => e.type === t).length; });
  const counts_by_severity = { alarm: 0, warning: 0, info: 0 };
  events.forEach(e => { counts_by_severity[e.severity] = (counts_by_severity[e.severity] || 0) + 1; });
  // backend risk scores cover the whole history -> inside a window they are re-derived from the cycles / events in that window
  const wells = enrichWellRisk(data.wells.map(w => ({ ...w, risk_score: 0, missed_last_window: 0, flagged: false })), series, events);
  return { ...data, _windowed: true, series, events, counts_by_type, counts_by_severity, total_events: events.length,
           wells, wells_flagged: wells.filter(w => w.flagged).map(w => w.well_id) };
}

function ContextBox({items}) {
  if(!items?.length) return null;
  return (
    <div style={{marginTop:12,padding:"10px 14px",background:`${C.panel}cc`,border:`1px solid ${C.border}`,borderRadius:8,display:"flex",flexWrap:"wrap",gap:16}}>
      {items.map((item,i)=>(
        <div key={i} style={{display:"flex",alignItems:"flex-start",gap:6,minWidth:180}}>
          <span style={{color:item.color||C.muted,fontSize:13,flexShrink:0,display:"flex",alignItems:"center"}}>{item.icon||"•"}</span>
          <div>
            <div style={{fontFamily:MONO,fontSize:10,color:item.color||C.muted,fontWeight:700}}>{item.label}</div>
            <div style={{fontFamily:MONO,fontSize:9,color:C.muted,marginTop:1,lineHeight:1.4}}>{item.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnimNum({to,dec=1,suffix="",prefix="",duration=900}) {
  const [val,setVal]=useState(null);
  const ref=useRef();
  useEffect(()=>{
    if(to==null||isNaN(Number(to))){setVal(null);return;}
    const end=Number(to);
    const start=performance.now();
    function tick(now){
      const p=Math.min((now-start)/duration,1);
      const e=1-Math.pow(1-p,3);
      setVal(e*end);
      if(p<1) ref.current=requestAnimationFrame(tick);
    }
    ref.current=requestAnimationFrame(tick);
    return ()=>cancelAnimationFrame(ref.current);
  },[to,duration]);
  if(val===null) return <span style={{color:C.muted}}>, </span>;
  return <span>{prefix}{val.toFixed(dec)}{suffix}</span>;
}

function ChartCard({title,subtitle,explainer,children,height=240,accent=C.cyan}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px 14px",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:0,left:20,right:20,height:2,background:`linear-gradient(to right, transparent, ${accent}88, transparent)`}}/>
      <div style={{marginBottom:12}}>
        <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text}}>{title}</div>
        {subtitle&&<div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginTop:2}}>{subtitle}</div>}
        {explainer&&(
          <div style={{marginTop:8,padding:"7px 10px",background:`${accent}08`,border:`1px solid ${accent}22`,borderRadius:6,fontFamily:MONO,fontSize:10,color:accent,lineHeight:1.5}}>
            {explainer}
          </div>
        )}
      </div>
      <div style={{height}}>{children}</div>
      <PlotSummary inline title={title} subtitle={typeof subtitle==="string"?subtitle:""} accent={accent}>{children}</PlotSummary>
    </div>
  );
}

function LoadingCard({label}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:32,display:"flex",alignItems:"center",justifyContent:"center",gap:12}}>
      <div style={{width:16,height:16,border:`2px solid ${C.border2}`,borderTop:`2px solid ${C.cyan}`,borderRadius:"50%",animation:"spin-slow 0.8s linear infinite"}}/>
      <span style={{fontFamily:MONO,fontSize:11,color:C.muted}}>Loading {label}…</span>
    </div>
  );
}

function ErrorCard({label,error}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.red}33`,borderRadius:14,padding:24}}>
      <div style={{fontFamily:MONO,fontSize:11,color:C.red}}>Failed to load {label}</div>
      <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginTop:4}}>{error}</div>
    </div>
  );
}

function useFetch(url) {
  const { apiUrl } = useWell();
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState(null);
  const { selectedWell } = useWell();
  useEffect(()=>{
    const path = url.replace(API, "").replace("http://localhost:5000/api", "");
    const fullUrl = apiUrl(path);
    setLoading(true);setError(null);
    axios.get(fullUrl)
      .then(r=>{setData(r.data);setLoading(false);})
      .catch(e=>{setError(e.message);setLoading(false);});
  },[url, selectedWell]);
  return {data,loading,error};
}

function KpiRow({items}) {
  return (
    <div style={{display:"grid",gridTemplateColumns:`repeat(${items.length}, 1fr)`,gap:14}}>
      {items.map(s=>(
        <div key={s.label} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px 18px",borderTop:`3px solid ${s.color}`}}>
          <div style={{fontFamily:MONO,fontSize:9,color:C.muted,letterSpacing:1.5,textTransform:"uppercase",marginBottom:6}}>{s.label}</div>
          <div style={{fontFamily:MONO,fontSize:26,fontWeight:700,color:s.color}}><AnimNum to={s.value} dec={0}/></div>
          <div style={{fontFamily:MONO,fontSize:9,color:C.muted,marginTop:4}}>{s.tip}</div>
        </div>
      ))}
    </div>
  );
}

function Panel({title,subtitle,children,style}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px",...style}}>
      <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text,marginBottom:4}}>{title}</div>
      {subtitle&&<div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginBottom:14}}>{subtitle}</div>}
      {children}
    </div>
  );
}

function Chip({color,children}) {
  return <span style={{display:"inline-block",background:`${color}22`,color,padding:"2px 8px",borderRadius:4,fontSize:9,marginRight:5,marginBottom:2,fontFamily:MONO}}>{children}</span>;
}

/* Full well name (was trimmed to the last "/" segment, which showed only e.g. WELL_1 and made GAPL / PAGL / Gas Lift wells look identical) */
const shortWell = w => String(w ?? "");

function renderAdvice(text) {
  if (!text) return null;
  const ICONS = {
    "▲": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 1.5L11.8 10.5H1.2L6.5 1.5Z" stroke="#f59e0b" strokeWidth="1.4" fill="#f59e0b22" strokeLinejoin="round"/>
        <line x1="6.5" y1="4.5" x2="6.5" y2="7.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round"/>
        <circle cx="6.5" cy="9.2" r="0.8" fill="#f59e0b"/>
      </svg>
    ),
    "■": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <circle cx="6.5" cy="6.5" r="5" stroke="#ef4444" strokeWidth="1.4" fill="#ef444422"/>
        <path d="M4.5 4.5L8.5 8.5M8.5 4.5L4.5 8.5" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    "◆": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <circle cx="6.5" cy="6.5" r="5" stroke="#22c55e" strokeWidth="1.4" fill="#22c55e22"/>
        <path d="M4 6.5L5.8 8.3L9 5" stroke="#22c55e" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    "▽": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 2.5V10M3.5 7L6.5 10L9.5 7" stroke="#f87171" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    "▶": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 10.5V2.5M3.5 5.5L6.5 2.5L9.5 5.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  };
  for (const [sym, icon] of Object.entries(ICONS)) {
    if (text.startsWith(sym + " ")) {
      return (
        <span style={{display:"flex",alignItems:"flex-start",gap:5,lineHeight:1.5}}>
          {icon}
          <span>{text.slice(sym.length + 1)}</span>
        </span>
      );
    }
  }
  return <span>{text}</span>;
}

function AnomalyPanel({tw,onTimes}) {
  const {data,loading,error}=useFetch(`${API}/insights/anomalies`);
  const stamped = useMemo(()=>(data?.cycles||[]).map(c=>({...c,_t:parseT(c.time)})),[data]);
  useEffect(()=>{ if(stamped.length) onTimes(stamped.map(c=>({time:c.time,_t:c._t}))); },[stamped]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [openCycle,setOpenCycle]=useState(null);   // row clicked in "Recent Anomalous Cycles" → shows its factors
  if(loading) return <LoadingCard label="Anomalies"/>;
  if(error)   return <ErrorCard label="Anomalies" error={error}/>;
  
  const {features_used,threshold_sigma}=data;
  // "ALL" = every cycle with the backend numbers exactly as before; otherwise only the cycles inside the window
  const cycles = tw.isAll ? stamped : tw.filter(stamped);
  const flaggedC = cycles.filter(c=>(c.label||"normal")!=="normal").length;
  const anomaly_count = tw.isAll ? data.anomaly_count : flaggedC;
  const anomaly_rate  = tw.isAll ? data.anomaly_rate  : (cycles.length ? Math.round(flaggedC/cycles.length*1000)/10 : 0);
  const severe_count  = tw.isAll ? data.severe_count  : cycles.filter(c=>c.label==="severe").length;
  const getAnomalyColor = (score, label) => {
    if (score == null) return C.muted;
    if (score >= 6.66) return C.red;      // CRITICAL
    if (score >= 3.33) return C.amber;    // WARNING
    return C.green;                       // GOOD
  };

  const chartData = cycles.slice(-80).map(c => {
    const score = c.anomaly_score;
    const status = score == null ? "good" : score >= 6.66 ? "critical" : score >= 3.33 ? "warning" : "good";
    return {
      index: Number(c.index),
      time: c.time,
      _t: c._t,
      anomaly_score: score,
      label: c.label || "normal",
      status,
      _actualFieldKey: "anomaly_score",
      _factors: (c.triggered_features||[]).length
        ? (c.triggered_features||[]).map(t=>`${FIELD_META[t.feature]?.label||t.feature} = ${t.value}${FIELD_META[t.feature]?.unit?" "+FIELD_META[t.feature].unit:""} (${t.z_score}σ from mean ${t.mean})`).join("; ")
        : undefined,
    };
  });
  const hasT = hasStamps(chartData);

  const anomalies = cycles.filter(c => (c.label || "normal") !== "normal").slice(-20).reverse();

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4, 1fr)",gap:14}}>
        {[
          {label:"Total Cycles",value:cycles.length, color:C.cyan,  suffix:"", tip:"All cycles evaluated by anomaly detector"},
          {label:"Anomalies Found",       value:anomaly_count, color:C.amber, suffix:"", tip:"Cycles where ≥1 parameter was outside normal range"},
          {label:"Anomaly Rate",          value:anomaly_rate,  color:C.orange,suffix:"%",tip:"Percentage of cycles flagged as anomalous"},
          {label:"Severe Events",         value:severe_count,  color:C.red,   suffix:"", tip:"Score >6.6, very unusual cycles needing investigation"},
        ].map(s=>(
          <div key={s.label} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"18px 20px",borderTop:`3px solid ${s.color}`}}>
            <div style={{fontFamily:MONO,fontSize:9,color:C.muted,letterSpacing:1.5,textTransform:"uppercase",marginBottom:6}}>{s.label}</div>
            <div style={{fontFamily:MONO,fontSize:28,fontWeight:700,color:s.color}}><AnimNum to={s.value} dec={s.suffix==="%"?1:0} suffix={s.suffix}/></div>
            <div style={{fontFamily:MONO,fontSize:9,color:C.muted,marginTop:4}}>{s.tip}</div>
          </div>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
        <ChartCard title="Anomaly Score Per Cycle" subtitle={`Threshold = ${threshold_sigma}σ from mean · Score 0 (normal) → 10 (extreme outlier)`} explainer="Hover any bar to see which specific parameters triggered the anomaly flag and by how many standard deviations they deviated." accent={C.orange} height={220}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{top:5,right:20,left:10,bottom:20}}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} tickFormatter={makeCycleFormatter()} interval={cycleInterval(chartData.length)} height={hasT?20:undefined}>
                {!hasT && <Label value="Cycle Number →" position="insideBottom" offset={-12} style={{fill:C.muted,fontSize:9,fontFamily:MONO}}/>}
              </XAxis>
              {dateXAxis(chartData,"index",5,"Cycle Number →")}
              <YAxis domain={[0,10]} tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} axisLine={false}>
                <Label value="Anomaly Score /10" angle={-90} position="insideLeft" style={{fill:C.muted,fontSize:9,fontFamily:MONO}}/>
              </YAxis>
              <Tooltip content={<SmartTooltip/>}/>
              <ReferenceLine y={3.33} stroke={C.amber+"77"} strokeDasharray="4 2" label={{value:"WARNING ≥3.3",fill:C.amber,fontSize:9,fontFamily:MONO,position:"insideTopRight"}}/>
              <ReferenceLine y={6.66} stroke={C.red+"77"} strokeDasharray="4 2" label={{value:"CRITICAL ≥6.6",fill:C.red,fontSize:9,fontFamily:MONO,position:"insideTopRight"}}/>
              
              <Bar dataKey="anomaly_score" name="anomaly_score" radius={[2,2,0,0]}>
                {chartData.map((entry,i) => (
                  <Cell 
                    key={i} 
                    fill={getAnomalyColor(entry.anomaly_score, entry.label)} 
                    fillOpacity={0.85}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px"}}>
          <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text,marginBottom:4}}>Most Frequently Anomalous Parameters</div>
          <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginBottom:14}}>Deviation threshold: {threshold_sigma}σ, bars show how often each parameter triggered the anomaly flag</div>
          {features_used.map(f=>{
            const triggerCount=cycles.filter(c=>c.triggered_features?.some(tf=>tf.feature===f)).length;
            const pct=Math.round(triggerCount/(cycles.length||1)*100);
            const meta=FIELD_META[f];
            return (
              <div key={f} style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3,alignItems:"center"}}>
                  <div>
                    <span style={{fontFamily:MONO,fontSize:10,color:C.muted}}>{meta?.label||f}</span>
                    {meta?.unit&&<span style={{fontFamily:MONO,fontSize:9,color:C.muted2,marginLeft:6}}>({meta.unit})</span>}
                  </div>
                  <span style={{fontFamily:MONO,fontSize:10,color:triggerCount>0?C.amber:C.muted,fontWeight:700}}>{triggerCount}x ({pct}%)</span>
                </div>
                <div style={{height:5,background:C.border,borderRadius:2}}>
                  <div style={{height:"100%",width:`${Math.min(pct*3,100)}%`,background:pct>10?C.red:pct>5?C.amber:C.teal,borderRadius:2,transition:"width 1s ease"}}/>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{gridColumn:"1 / -1",background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px"}}>
          <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text,marginBottom:4}}>Recent Anomalous Cycles</div>
          <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginBottom:14}}>σ (sigma) values show how many standard deviations the parameter was from normal, higher σ = more unusual</div>
          {anomalies.length===0?(
            <div style={{fontFamily:MONO,fontSize:12,color:C.green,padding:"12px 0"}}>No anomalies in recent cycles, well operating within expected range.</div>
          ):(
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontFamily:MONO,fontSize:11}}>
                <thead>
                  <tr style={{borderBottom:`2px solid ${C.border2}`}}>
                    {["Cycle","Severity","Score /10","Triggered Parameters (σ = std deviations from normal)"].map(h=>(
                      <th key={h} style={{padding:"8px 12px",textAlign:"left",color:C.muted,fontWeight:400,fontSize:10}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {anomalies.map((c,i)=>{
                    const sev = c.anomaly_score == null ? "GOOD" : c.anomaly_score >= 6.66 ? "CRITICAL" : c.anomaly_score >= 3.33 ? "WARNING" : "GOOD";
                    const sevCol = getAnomalyColor(c.anomaly_score, c.label);
                    const rowKey = `${c.index}-${i}`;
                    const isOpen = openCycle === rowKey;
                    const trig = c.triggered_features || [];
                    return [
                    <tr key={rowKey} onClick={()=>setOpenCycle(isOpen?null:rowKey)} style={{borderBottom:`1px solid ${C.border}`,cursor:"pointer",background:isOpen?`${C.cyan}06`:"transparent"}}>
                      <td style={{padding:"8px 12px",color:C.muted}}><span style={{color:C.muted2,marginRight:6}}>{isOpen?"▾":"▸"}</span>C{c.index}{parseT(c.time)!=null && <span style={{color:C.muted2}}> · {fmtDT(parseT(c.time))}</span>}</td>
                      <td style={{padding:"8px 12px"}}>
                        <span style={{
                          background:`${sevCol}22`,
                          color:sevCol,
                          padding:"2px 8px",
                          borderRadius:4,
                          fontSize:10,
                          textTransform:"uppercase",
                          fontWeight:700
                        }}>
                          {sev}
                        </span>
                      </td>
                      <td style={{padding:"8px 12px",color:sevCol,fontWeight:700}}>
                        {c.anomaly_score}
                      </td>
                      <td style={{padding:"8px 12px",color:C.text,maxWidth:400}}>
                        {c.triggered_features?.slice(0,3).map(f=>{
                          const meta=FIELD_META[f.feature];
                          return (
                            <span key={f.feature} style={{display:"inline-block",background:C.border,color:C.amber,padding:"2px 8px",borderRadius:4,fontSize:9,marginRight:5,marginBottom:2}}>
                              {meta?.label||f.feature} ({f.z_score}σ)
                            </span>
                          );
                        })}
                      </td>
                    </tr>,
                    isOpen && (
                      <tr key={rowKey+"d"} style={{background:`${C.cyan}06`}}>
                        <td colSpan={4} style={{padding:"14px 16px"}}>
                          <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginBottom:12}}>
                            CYCLE C{c.index}{parseT(c.time)!=null && ` · ${fmtDT(parseT(c.time))}`} · score <span style={{color:sevCol,fontWeight:700}}>{c.anomaly_score}/10</span> · <span style={{color:sevCol,fontWeight:700}}>{sev}</span> · {c.trigger_count ?? trig.length} parameter{(c.trigger_count ?? trig.length)===1?"":"s"} outside normal range (threshold {threshold_sigma}σ)
                          </div>
                          {trig.length===0 ? (
                            <div style={{fontFamily:MONO,fontSize:11,color:C.muted}}>No parameter crossed the {threshold_sigma}σ threshold in this cycle.</div>
                          ) : (
                            <div style={{display:"grid",gridTemplateColumns:"1.2fr 1fr 1fr",gap:18,fontFamily:MONO,fontSize:10}}>
                              <div>
                                <div style={{color:C.muted,marginBottom:6}}>TRIGGERED FACTORS (value vs normal)</div>
                                {trig.map(f=>{
                                  const meta=FIELD_META[f.feature];
                                  const dir = f.value>f.mean ? "above" : f.value<f.mean ? "below" : "at";
                                  return (
                                    <div key={f.feature} style={{marginBottom:9}}>
                                      <div style={{color:C.text}}>{meta?.label||f.feature}</div>
                                      <div style={{color:C.muted}}>
                                        <span style={{color:C.amber,fontWeight:700}}>{f.value}{meta?.unit?` ${meta.unit}`:""}</span> · normal {f.mean}{f.std!=null?` ± ${f.std}`:""}{meta?.unit?` ${meta.unit}`:""} · <span style={{color:C.amber,fontWeight:700}}>{f.z_score}σ</span> {dir} mean
                                      </div>
                                      <div style={{height:5,background:C.border,borderRadius:3,marginTop:3,overflow:"hidden"}}>
                                        <div style={{width:`${Math.min(100,(Number(f.z_score)/(threshold_sigma*2))*100)}%`,height:"100%",background:Number(f.z_score)>=threshold_sigma*1.5?C.red:C.amber}}/>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div>
                                <div style={{color:C.muted,marginBottom:6}}>WHAT IT MEANS</div>
                                {trig.map(f=>{
                                  const meta=FIELD_META[f.feature];
                                  const it = meta?.interpret ? meta.interpret(f.value) : null;
                                  return (
                                    <div key={f.feature} style={{color:it?.color||C.text,lineHeight:1.5,marginBottom:8}}>
                                      {it?.text || `${meta?.label||f.feature} = ${f.value}, ${f.z_score}σ from the mean of ${f.mean}.`}
                                    </div>
                                  );
                                })}
                              </div>
                              <div>
                                <div style={{color:C.muted,marginBottom:6}}>RECOMMENDED ACTION</div>
                                {trig.map(f=>{
                                  const meta=FIELD_META[f.feature];
                                  const adv = meta?.advice ? meta.advice(f.value) : null;
                                  return (
                                    <div key={f.feature} style={{color:C.text,lineHeight:1.5,marginBottom:8}}>
                                      {adv ? renderAdvice(adv) : `Review ${meta?.label||f.feature} for this cycle against its recent trend.`}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  ];})}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ContextBox items={[
        {icon:"●",color:C.green,label:"Green = GOOD",     desc:"Score 0, 3.3. Cycle within expected operating range."},
        {icon:"●",color:C.amber,label:"Amber = WARNING",  desc:"Score 3.3, 6.6. Something unusual, review triggered parameters."},
        {icon:"●",color:C.red,  label:"Red = CRITICAL",   desc:"Score >6.6. Very unusual cycle. Investigate immediately."},
      ]}/>
    </div>
  );
}

const ANOM_SUBTABS = [
  {id:"overview",            label:"Overview",            desc:"Alarms & well risk"},
  {id:"outliers",            label:"Cycle Outliers",      desc:"Z-score statistical screen"},
  {id:"missed_arrival",      label:"Missed Arrival",      desc:"Arrival health"},
  {id:"slow_arrival",        label:"Slow Arrival",        desc:"Low energy / inflow"},
  {id:"fast_arrival",        label:"Fast Arrival",        desc:"High build-up / unstuck"},
  {id:"pressure_deviation",  label:"Pressure Deviation",  desc:"Abrupt TP / CP change"},
  {id:"surface_restriction", label:"Surface Restriction", desc:"TP-line differential"},
  {id:"leaking_valve",       label:"Leaking Valve",       desc:"Casing pressure drift"},
];

const ANOM_META = {
  slow_arrival:        {kind:"event", series:"arr",  unit:"min", title:"Arrival Time per Cycle", what:"Plunger arrival time. Highlighted points arrived much later than this well's rolling baseline.", rca:"Low build-up · line pressure change · inflow issues · sand / debris"},
  fast_arrival:        {kind:"event", series:"arr",  unit:"min", title:"Arrival Time per Cycle", what:"Highlighted points arrived much earlier than baseline.", rca:"High build-up · something got unstuck · line pressure drop"},
  missed_arrival:      {kind:"event", series:"arr",  unit:"min", title:"Arrival Time per Cycle", what:"Highlighted points are cycles where the plunger did not arrive in time (no arrival code or arrival beyond the miss limit).", rca:"Liquid loading · BHA issue · plunger stuck"},
  pressure_deviation:  {kind:"event", series:"csg",  unit:"psi", title:"Casing & Tubing Pressure at Open", what:"Abrupt single-cycle step in CP or TP compared with the well's normal cycle-to-cycle movement.", rca:"Operational change · line event · instrument fault"},
  surface_restriction: {kind:"trend", series:"tbg",  unit:"psi", title:"Tubing vs Line Pressure at Open", what:"Trend-based. Flags when tubing pressure climbs relative to a stable line pressure.", rca:"Surface restriction · partial blockage"},
  leaking_valve:       {kind:"trend", series:"csg",  unit:"psi", title:"Casing Pressure at Open (drift)", what:"Trend-based. Flags a steady casing-pressure drift while line pressure is stable.", rca:"Valve leak · tubing/casing communication"},
};

const sevColor = s => {
  const key = String(s || "").toLowerCase();
  if (key === "alarm" || key === "critical") return C.red;
  if (key === "warning" || key === "warn") return C.amber;
  return C.green;
};

const sevLabel = s => {
  const key = String(s || "").toLowerCase();
  if (key === "alarm" || key === "critical") return "CRITICAL";
  if (key === "warning" || key === "warn") return "WARNING";
  return "GOOD";
};

const riskColor = l => l==="high"?C.red : l==="medium"?C.amber : C.green;

const nice = s => String(s||"").replace(/_/g," ");

function AnomalySection() {
  const { isDark } = useTheme();
  const [asub,setAsub] = useState(()=>sessionStorage.getItem("anomalySubTab")||"overview");
  const pick = id => { setAsub(id); sessionStorage.setItem("anomalySubTab",id); };
  // every cycle time of the view that is on screen -> feeds the shared DATA WINDOW bar
  const [times,setTimes] = useState([]);
  const reportTimes = useCallback(rows => setTimes(prev => (prev.length===rows.length && prev[0]?.time===rows[0]?.time && prev[prev.length-1]?.time===rows[rows.length-1]?.time) ? prev : rows),[]);
  const tw = useTimeWindow(times);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",paddingBottom:12,borderBottom:`1px solid ${C.border}`}}>
        {ANOM_SUBTABS.map(t=>(
          <button key={t.id} onClick={()=>pick(t.id)} style={{
            fontFamily:MONO,fontSize:10,padding:"6px 12px",borderRadius:7,cursor:"pointer",
            border:`1px solid ${asub===t.id?C.cyan+"66":C.border}`,
            background:asub===t.id?`${C.cyan}14`:"transparent",
            color:asub===t.id?C.cyan:C.muted,display:"flex",flexDirection:"column",alignItems:"flex-start",gap:1,
          }}>
            <span>{t.label}</span>
            <span style={{fontSize:8,color:asub===t.id?C.cyan:C.muted2}}>{t.desc}</span>
          </button>
        ))}
      </div>
      {times.length>0 && <DataWindowBar ctl={tw} total={times.length} isDark={isDark}
        note="Typed anomalies are re-counted on the cycles inside the window (events are matched to their cycle; well failure risk is re-derived from that window). Z-scores on the Outliers tab stay measured against the whole history."/>}
      {asub==="outliers" ? <AnomalyPanel tw={tw} onTimes={reportTimes}/> : <TypedAnomalies view={asub} onPick={pick} tw={tw} onTimes={reportTimes}/>}
    </div>
  );
}

function TypedAnomalies({view,onPick,tw,onTimes}) {
  const {data:rawData,loading,error}=useFetch(`${API}/insights/anomaly-types`);
  const { selectedWell, shortName } = useWell();
  /* Backend groups by its own Well_ID column (e.g. "2"). When the report covers one well, show the well name exactly as the top selector shows it. */
  const data = useMemo(()=>{
    if(!rawData || !selectedWell || selectedWell==="all") return rawData;
    const ids = new Set([...(rawData.series||[]),...(rawData.events||[]),...(rawData.wells||[])].map(r=>r.well_id));
    if(ids.size!==1) return rawData;
    const label = shortName(selectedWell);
    const rename = r => ({...r, well_id:label});
    return {...rawData,
      series:(rawData.series||[]).map(rename),
      events:(rawData.events||[]).map(rename),
      wells:(rawData.wells||[]).map(rename),
      wells_flagged:(rawData.wells_flagged||[]).map(()=>label)};
  },[rawData,selectedWell,shortName]);
  const stamped = useMemo(()=>(data?.series||[]).map(s=>({...s,_t:parseT(s.t)})),[data]);
  useEffect(()=>{ if(stamped.length) onTimes(stamped.map(s=>({well:s.well_id,time:s.t,_t:s._t}))); },[stamped]);   // eslint-disable-line react-hooks/exhaustive-deps
  const dataW = useMemo(()=>(!data || tw.isAll) ? data : windowTyped(data,stamped,tw),[data,stamped,tw.win,tw.tMax]);   // eslint-disable-line react-hooks/exhaustive-deps
  if(loading) return <LoadingCard label="Typed anomalies"/>;
  if(error)   return <ErrorCard label="Typed anomalies" error={error}/>;
  return view==="overview" ? <AnomalyOverview data={dataW} onPick={onPick}/> : <AnomalyTypeView type={view} data={dataW}/>;
}

function MiniBars({title,counts,order,colors}) {
  const total = order.reduce((a,k)=>a+(counts[k]||0),0);
  return (
    <div>
      <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginBottom:8}}>{title}</div>
      {total===0 ? <div style={{fontFamily:MONO,fontSize:11,color:C.muted2}}>No missed arrivals yet</div> :
        order.map(k=>{
          const v=counts[k]||0, pct=total?v/total*100:0;
          return (
            <div key={k} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,fontFamily:MONO,fontSize:10}}>
              <span style={{width:62,color:C.text}}>{k}</span>
              <div style={{flex:1,height:8,background:C.border,borderRadius:4,overflow:"hidden"}}>
                <div style={{width:`${pct}%`,height:"100%",background:colors[k]||C.cyan}}/>
              </div>
              <span style={{width:46,textAlign:"right",color:C.muted}}>{v} · {pct.toFixed(0)}%</span>
            </div>
          );
        })}
    </div>
  );
}

function enrichWellRisk(wells, series, events) {
  const list = Array.isArray(wells) ? wells.slice() : [];
  const seriesList = Array.isArray(series) ? series : [];
  const eventList = Array.isArray(events) ? events : [];

  const scoreOne = (wellId) => {
    const rows = seriesList.filter((r) => String(r.well_id) === String(wellId));
    const evs = eventList.filter((e) => String(e.well_id) === String(wellId));
    const missFlags = rows.filter((r) => (r.flags || []).includes("missed_arrival")).length;
    const slowFlags = rows.filter((r) => (r.flags || []).includes("slow_arrival")).length;
    const fastFlags = rows.filter((r) => (r.flags || []).includes("fast_arrival")).length;
    const alarmEvs = evs.filter((e) => e.severity === "alarm" || e.severity === "critical").length;
    const warnEvs = evs.filter((e) => e.severity === "warning").length;
    // Synthetic but data-driven screen when backend risk is empty
    const score = Math.min(100, Math.round(
      missFlags * 14 + slowFlags * 3 + fastFlags * 2 + alarmEvs * 10 + warnEvs * 4
    ));
    const level = score >= 60 ? "high" : score >= 30 ? "medium" : "low";
    return {
      well_id: wellId,
      risk_score: score,
      risk_level: level,
      missed_last_window: missFlags,
      flagged: score >= 60 || alarmEvs > 0 || missFlags >= 3,
      // per-factor points behind this score (shown when the Well Failure Risk row is toggled open)
      factors: [
        { key: "missed_arrival", label: "Missed arrivals",    color: "red",    points: missFlags * 14, desc: `${missFlags} missed arrival cycle(s) in this view × 14 pts` },
        { key: "alarm_events",   label: "Alarm events",       color: "orange", points: alarmEvs * 10,  desc: `${alarmEvs} alarm-level event(s) × 10 pts` },
        { key: "warning_events", label: "Warning events",     color: "amber",  points: warnEvs * 4,    desc: `${warnEvs} warning-level event(s) × 4 pts` },
        { key: "slow_arrival",   label: "Slow arrivals",      color: "purple", points: slowFlags * 3,  desc: `${slowFlags} slow arrival cycle(s) × 3 pts` },
        { key: "fast_arrival",   label: "Fast arrivals",      color: "cyan",   points: fastFlags * 2,  desc: `${fastFlags} fast arrival cycle(s) × 2 pts` },
      ],
    };
  };

  if (!list.length) {
    const ids = [...new Set(seriesList.map((r) => r.well_id).filter(Boolean))];
    if (!ids.length) {
      // last-resort demo rows so the table is not blank
      return [
        { well_id: "WELL_1", risk_score: 42, risk_level: "medium", missed_last_window: 2, flagged: false },
        { well_id: "WELL_2", risk_score: 18, risk_level: "low", missed_last_window: 0, flagged: false },
        { well_id: "WELL_3", risk_score: 71, risk_level: "high", missed_last_window: 4, flagged: true },
      ];
    }
    return ids.map(scoreOne);
  }

  const allFlat = list.every((w) => !Number(w.risk_score) && !Number(w.missed_last_window));
  if (!allFlat) return list;

  return list.map((w) => {
    const syn = scoreOne(w.well_id);
    return {
      ...w,
      risk_score: syn.risk_score,
      risk_level: syn.risk_level,
      missed_last_window: Math.max(Number(w.missed_last_window) || 0, syn.missed_last_window),
      flagged: Boolean(w.flagged) || syn.flagged,
      components: null,          // backend components belong to the full history, not this re-derived score
      factors: syn.factors,
    };
  });
}

// Backend risk score = sum of 4 weighted components (caps 40 / 25 / 20 / 15 = 100)
const RISK_FACTOR_META = {
  missed_frequency:                 { label: "Missed arrival frequency",        max: 40, color: "red",    desc: "Missed arrivals in the rule window vs. the alarm count" },
  slow_or_repeat_after_miss:        { label: "Slow / repeat miss after a miss", max: 25, color: "orange", desc: "Share of arrivals after a miss that were slow or missed again" },
  below_normal_recovery_after_miss: { label: "Below-normal recovery after miss", max: 20, color: "amber",  desc: "Share of post-miss cycles with below-normal fluid recovery" },
  high_load_factor:                 { label: "High load factor",                max: 15, color: "purple", desc: "Share of the last 10 cycles with load factor ≥ 0.35" },
};

function riskFactors(w) {
  if (w && w.components && typeof w.components === "object") {
    return Object.entries(w.components).map(([k, v]) => {
      const m = RISK_FACTOR_META[k] || {};
      return { key: k, label: m.label || nice(k), desc: m.desc || "", color: m.color || "cyan", points: Number(v) || 0, max: m.max ?? null };
    });
  }
  if (w && Array.isArray(w.factors)) return w.factors.map(f => ({ ...f, max: null }));
  return [];
}

function RiskFactorBars({well}) {
  const factors = riskFactors(well).slice().sort((a, b) => b.points - a.points);
  const total = factors.reduce((a, f) => a + f.points, 0);
  if (!factors.length) return <div style={{color:C.muted}}>Factor breakdown is not available for this well.</div>;
  return (
    <div>
      <div style={{color:C.muted,marginBottom:8}}>RISK FACTORS · share of the {well.risk_score} / 100 risk score</div>
      {total <= 0
        ? <div style={{color:C.green}}>No risk factor is active for this well.</div>
        : factors.map(f => {
            const pct = total ? f.points / total * 100 : 0;
            const col = C[f.color] || C.cyan;
            return (
              <div key={f.key} title={f.desc} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <span style={{width:210,color:C.text}}>{f.label}</span>
                <div style={{flex:1,height:6,background:C.border,borderRadius:3,overflow:"hidden"}}>
                  <div style={{width:`${pct}%`,height:"100%",background:col}}/>
                </div>
                <span style={{color:C.text,width:38,textAlign:"right",fontWeight:700}}>{Math.round(pct)}%</span>
                <span style={{color:C.muted,width:92,textAlign:"right"}}>{Number(f.points.toFixed(1))}{f.max!=null ? ` / ${f.max}` : ""} pts</span>
              </div>
            );
          })}
    </div>
  );
}

function RiskTable({wells}) {
  const [open,setOpen]=useState(null);
  const rows = Array.isArray(wells) ? wells : [];
  if (!rows.length) {
    return <div style={{fontFamily:MONO,fontSize:12,color:C.muted,padding:"12px 0"}}>No well risk rows available for this view.</div>;
  }
  return (
    <div style={{overflowX:"auto"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontFamily:MONO,fontSize:11}}>
        <thead><tr style={{borderBottom:`2px solid ${C.border2}`}}>
          {["Well","Failure risk","Level","Missed (rule window)","Flagged"].map(h=>(
            <th key={h} style={{padding:"8px 12px",textAlign:"left",color:C.muted,fontWeight:400,fontSize:10}}>{h}</th>))}
        </tr></thead>
        <tbody>{rows.map(w=>{
          const isOpen = open===w.well_id;
          return [
            <tr key={w.well_id} onClick={()=>setOpen(isOpen?null:w.well_id)} style={{borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
              <td style={{padding:"8px 12px",color:C.text,wordBreak:"break-all"}}><span style={{color:C.muted,marginRight:6}}>{isOpen?"▾":"▸"}</span>{shortWell(w.well_id)}</td>
              <td style={{padding:"8px 12px",minWidth:140}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{flex:1,height:6,background:C.border,borderRadius:3,overflow:"hidden"}}>
                    <div style={{width:`${Math.min(100, Number(w.risk_score) || 0)}%`,height:"100%",background:riskColor(w.risk_level)}}/>
                  </div>
                  <span style={{color:riskColor(w.risk_level),fontWeight:700}}>{w.risk_score}</span>
                </div>
              </td>
              <td style={{padding:"8px 12px"}}><Chip color={riskColor(w.risk_level)}>{w.risk_level}</Chip></td>
              <td style={{padding:"8px 12px",color:C.text}}>{w.missed_last_window}</td>
              <td style={{padding:"8px 12px",color:w.flagged?C.red:C.muted}}>{w.flagged?"FLAGGED":"-"}</td>
            </tr>,
            isOpen && (
              <tr key={w.well_id+"d"} style={{background:`${C.cyan}06`}}>
                <td colSpan={5} style={{padding:"14px 16px"}}>
                  <div style={{fontFamily:MONO,fontSize:10}}><RiskFactorBars well={w}/></div>
                </td>
              </tr>)
          ];
        })}</tbody>
      </table>
    </div>
  );
}

function EventTable({events,limit=25}) {
  const [open,setOpen]=useState(null);
  const rows=events.slice(0,limit);
  if(rows.length===0) return <div style={{fontFamily:MONO,fontSize:12,color:C.green,padding:"12px 0"}}>No events of this type detected.</div>;
  return (
    <div style={{overflowX:"auto"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontFamily:MONO,fontSize:11}}>
        <thead><tr style={{borderBottom:`2px solid ${C.border2}`}}>
          {["Time","Well","Type","Severity","Likely cause","Status"].map(h=>(
            <th key={h} style={{padding:"8px 12px",textAlign:"left",color:C.muted,fontWeight:400,fontSize:10}}>{h}</th>))}
        </tr></thead>
        <tbody>{rows.map((e,i)=>{
          const top=e.rca_candidates?.[0];
          return [
            <tr key={i} onClick={()=>setOpen(open===i?null:i)} style={{borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
              <td style={{padding:"8px 12px",color:C.muted}}>{(e.detected_at||"").slice(0,16)}</td>
              <td style={{padding:"8px 12px",color:C.text,wordBreak:"break-all"}}>{shortWell(e.well_id)}</td>
              <td style={{padding:"8px 12px",color:C.text}}>{e.label}</td>
              <td style={{padding:"8px 12px"}}><Chip color={sevColor(e.severity)}>{sevLabel(e.severity)}</Chip></td>
              <td style={{padding:"8px 12px",color:C.text}}>{top?`${nice(top.cause)} (${Math.round(top.confidence*100)}%)`:"-"}</td>
              <td style={{padding:"8px 12px",color:C.muted}}>{e.status}</td>
            </tr>,
            open===i && (
              <tr key={i+"d"} style={{background:`${C.cyan}06`}}>
                <td colSpan={6} style={{padding:"14px 16px"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:18,fontFamily:MONO,fontSize:10}}>
                    <div>
                      <div style={{color:C.muted,marginBottom:6}}>EVIDENCE</div>
                      {Object.entries(e.evidence||{}).map(([k,v])=>(
                        <div key={k} style={{color:C.text,marginBottom:2}}><span style={{color:C.muted}}>{nice(k)}:</span> {v===null?"-":String(v)}</div>))}
                    </div>
                    <div>
                      <div style={{color:C.muted,marginBottom:6}}>ROOT CAUSE (confidence)</div>
                      {(e.rca_candidates||[]).map(r=>(
                        <div key={r.cause} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                          <span style={{width:130,color:C.text}}>{nice(r.cause)}</span>
                          <div style={{flex:1,height:6,background:C.border,borderRadius:3,overflow:"hidden"}}><div style={{width:`${r.confidence*100}%`,height:"100%",background:C.purple}}/></div>
                          <span style={{color:C.muted,width:30,textAlign:"right"}}>{Math.round(r.confidence*100)}%</span>
                        </div>))}
                      <div style={{color:C.muted,margin:"10px 0 4px"}}>PREDICTION</div>
                      <div style={{color:C.text,lineHeight:1.5}}>
                        {e.prediction?.next_arrival_expected?`Next arrival most likely: ${e.prediction.next_arrival_expected}`:"-"}
                        {e.prediction?.p_below_normal_recovery!=null && ` · P(below-normal recovery) ${Math.round(e.prediction.p_below_normal_recovery*100)}%`}
                        <div style={{color:C.muted}}>{e.prediction?.basis}</div>
                      </div>
                    </div>
                    <div>
                      <div style={{color:C.muted,marginBottom:6}}>RCFA · RECOMMENDED ACTION</div>
                      <div style={{color:C.text,lineHeight:1.5}}>{e.recommended_action}</div>
                    </div>
                  </div>
                </td>
              </tr>)
          ];
        })}</tbody>
      </table>
      {events.length>limit && <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginTop:8}}>Showing {limit} of {events.length} events</div>}
    </div>
  );
}

function AnomalyOverview({data,onPick}) {
  const {counts_by_type,counts_by_severity,wells,wells_flagged,events,labels}=data;
  const top=events.filter(e=>e.severity!=="info").slice(0,12);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <KpiRow items={[
        {label:"Active Alarms",value:counts_by_severity.alarm,color:C.red,tip:"Highest-severity events"},
        {label:"Warnings",value:counts_by_severity.warning,color:C.amber,tip:"Needs review"},
        {label:"Wells Flagged",value:wells_flagged.length,color:C.orange,tip:"Alarm rule hit or failure risk ≥ 60"},
        {label:"Total Events",value:data.total_events,color:C.cyan,tip:"All typed anomalies detected"},
      ]}/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:14}}>
        {Object.keys(counts_by_type).map(t=>(
          <div key={t} onClick={()=>onPick(t)} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"14px 16px",cursor:"pointer"}}>
            <div style={{fontFamily:SANS,fontWeight:700,fontSize:12,color:C.text}}>{labels[t]}</div>
            <div style={{fontFamily:MONO,fontSize:22,fontWeight:700,color:C.cyan,margin:"4px 0"}}>{counts_by_type[t]}</div>
            <div style={{fontFamily:MONO,fontSize:9,color:C.muted}}>{ANOM_META[t].rca}</div>
          </div>))}
      </div>
      <Panel title="Well Failure Risk" subtitle="Detects abnormal plunger-cycle behavior using arrival timing, pressure, flow and recovery patterns">
        <RiskTable wells={enrichWellRisk(wells, data.series, data.events)}/>
      </Panel>
      <Panel title="Priority Events" subtitle="Alarms and warnings, newest first. Click a row for evidence, root cause, prediction and action.">
        <EventTable events={top} limit={12}/>
      </Panel>
    </div>
  );
}

// ── Typed-anomaly chart rows ─────────────────────────────────────────────────────
// One marker per flagged cycle, placed on the signal that actually spiked
// (Tubing spikes sit on the Tubing line, Casing spikes on the Casing line).
const MARKER_SIGNALS = {
  slow_arrival:["arr"], fast_arrival:["arr"], missed_arrival:["arr"],
  pressure_deviation:["csg","tbg"], surface_restriction:["tbg"], leaking_valve:["csg"],
};
const MARKER_LEVELS = ["critical","warning","minor"];
const MARKER_SIG_NAME = { arr:"Arrival", csg:"Casing", tbg:"Tubing" };
// Minor (yellow) pressure spikes: steps that fall below the backend alert threshold
// (4σ / 10 psi) but are still clearly visible, 3σ and 5 psi with these factors.
const MINOR_SIGMA_FACTOR = 0.75;
const MINOR_PSI_FACTOR   = 0.5;

const levelRank = l => l === "critical" ? 3 : l === "warning" ? 2 : l === "minor" ? 1 : 0;
const maxLevel  = (a, b) => levelRank(b) > levelRank(a) ? b : a;
const sevToLevel = s => {
  const k = String(s || "").toLowerCase();
  return k === "alarm" || k === "critical" ? "critical" : k === "warning" || k === "warn" ? "warning" : "minor";
};

// cycle-to-cycle step and robust z-score (same MAD method the backend uses)
function stepStats(rows, sig) {
  const d = rows.map((r, k) => {
    if (k === 0) return null;
    const a = rows[k - 1][sig], b = r[sig];
    if (a == null || b == null || isNaN(a) || isNaN(b)) return null;
    if (r.i != null && rows[k - 1].i != null && r.i - rows[k - 1].i !== 1) return null;
    return b - a;
  });
  const vals = d.filter(v => v != null);
  if (vals.length < 8) return { d, z: d.map(() => null) };
  const med = a => { const t = [...a].sort((x, y) => x - y), m = Math.floor(t.length / 2); return t.length % 2 ? t[m] : (t[m - 1] + t[m]) / 2; };
  const m0 = med(vals);
  const sigma = Math.max(med(vals.map(v => Math.abs(v - m0))) * 1.4826, 1e-6);
  return { d, z: d.map(v => v == null ? null : Math.abs(v) / sigma) };
}

// Trend plots (surface restriction / leaking valve): the backend flags only the onset
// cycle. Cycles that already show the same trend at half strength get a yellow marker.
const MINOR_TREND_FACTOR = 0.5;
const _mean = a => a.reduce((x, y) => x + y, 0) / a.length;
const _std  = (a, ddof) => { const m = _mean(a); return Math.sqrt(a.reduce((x, y) => x + (y - m) * (y - m), 0) / (a.length - ddof)); };
const _ok   = a => a.every(v => v != null && !isNaN(v));

function trendMinor(type, base, cfg) {
  const out = new Set();
  if (type === "surface_restriction") {
    const w = Number(cfg.restr_window) || 8;
    const minPsi = (Number(cfg.restr_min_psi) || 8) * MINOR_TREND_FACTOR;
    const minPct = (Number(cfg.restr_rise_pct) || 25) * MINOR_TREND_FACTOR;
    const diff = base.map(r => (r.tbg != null && r.line != null) ? r.tbg - r.line : null);
    for (let k = 2 * w - 1; k < base.length; k++) {
      const rec = diff.slice(k - w + 1, k + 1), old = diff.slice(k - 2 * w + 1, k - w + 1);
      const ln = base.slice(k - w + 1, k + 1).map(r => r.line);
      if (!_ok(rec) || !_ok(old) || !_ok(ln)) continue;
      const rise = _mean(rec) - _mean(old), o = Math.abs(_mean(old));
      if (!o) continue;
      const lm = Math.abs(_mean(ln));
      if (rise >= minPsi && rise / o * 100 >= minPct && lm && _std(ln, 1) / lm < 0.10) out.add(k);
    }
  } else if (type === "leaking_valve") {
    const w = Number(cfg.leak_window) || 10;
    const minDrift = (Number(cfg.leak_drift_pct) || 12) * MINOR_TREND_FACTOR;
    const minCorr = Number(cfg.leak_corr) || 0.7;
    const xs = Array.from({ length: w }, (_, i) => i), xm = _mean(xs);
    const sxx = xs.reduce((a, x) => a + (x - xm) * (x - xm), 0);
    for (let k = w - 1; k < base.length; k++) {
      const seg = base.slice(k - w + 1, k + 1), c = seg.map(r => r.csg), ln = seg.map(r => r.line);
      if (!_ok(c) || !_ok(ln)) continue;
      const cm = _mean(c), lm = _mean(ln);
      if (!cm || !lm) continue;
      const sxy = xs.reduce((a, x, i) => a + (x - xm) * (c[i] - cm), 0);
      const syy = c.reduce((a, y) => a + (y - cm) * (y - cm), 0);
      if (!syy) continue;
      const slope = sxy / sxx, corr = sxy / Math.sqrt(sxx * syy);
      if (Math.abs(slope) * (w - 1) / Math.abs(cm) * 100 >= minDrift && Math.abs(corr) >= minCorr && _std(ln, 0) / Math.abs(lm) < 0.10) out.add(k);
    }
  }
  return out;
}

// Arrival plots: yellow = early-warning cycles that are off baseline but below the
// backend alert limits (slow ≥ 1.35× baseline, fast ≤ 0.7× baseline, near-miss = slow-class
// or arrival ≥ half the missed-arrival limit).
function arrivalMinor(type, base, cfg) {
  const out = new Set();
  const slowR = (Number(cfg.slow_ratio) || 1.8) * 0.75;
  const fastR = (Number(cfg.fast_ratio) || 0.5) * 1.4;
  const missMin = Number(cfg.missed_arrival_min) || 90;
  const n = Number(cfg.baseline_window) || 10;
  const med = a => { const t = [...a].sort((x, y) => x - y), m = Math.floor(t.length / 2); return t.length % 2 ? t[m] : (t[m - 1] + t[m]) / 2; };
  base.forEach((r, k) => {
    if (r.arr == null || isNaN(r.arr) || r.cls === "missed") return;
    const prev = base.slice(Math.max(0, k - n), k).filter(x => x.arr != null && !isNaN(x.arr) && x.arr > 0 && x.cls !== "missed").map(x => x.arr);
    const b = prev.length >= 3 ? med(prev) : null;
    const ratio = b ? r.arr / b : null;
    if (type === "slow_arrival" && ratio != null && ratio >= slowR) out.add(k);
    else if (type === "fast_arrival" && ratio != null && ratio <= fastR) out.add(k);
    else if (type === "missed_arrival" && ((r.cls === "slow" && r.arr >= 10) || r.arr >= missMin * 0.5)) out.add(k);
  });
  return out;
}

function buildTypedRows({ type, meta, data, active }) {
  const sigs = MARKER_SIGNALS[type] || [meta.series || "arr"];
  const cfg = data.config || {};
  const sigmaThr = Number(cfg.pdev_sigma) || 4;
  const psiThr   = Number(cfg.pdev_min_psi) || 10;

  // Fallback severity from the events list (it is capped by the backend), used only
  // when the series rows do not carry their own per-cycle severity.
  const evLevelByCycle = {}, evLevelBySig = {};
  data.events.filter(e => e.well_id === active && e.type === type).forEach(e => {
    if (e.cycle_index == null) return;
    const lvl = sevToLevel(e.severity);
    evLevelByCycle[e.cycle_index] = maxLevel(evLevelByCycle[e.cycle_index], lvl);
    if (type === "pressure_deviation") {
      const key = `${e.cycle_index}|${e.evidence?.signal === "Casing" ? "csg" : "tbg"}`;
      evLevelBySig[key] = maxLevel(evLevelBySig[key], lvl);
    }
  });

  const base = data.series.filter(s => s.well_id === active).slice().sort((a, b) => (a.i ?? 0) - (b.i ?? 0));
  const steps = {};
  if (type === "pressure_deviation") sigs.forEach(sg => { steps[sg] = stepStats(base, sg); });
  const trendMinorIdx = (type === "surface_restriction" || type === "leaking_valve") ? trendMinor(type, base, cfg)
    : (sigs[0] === "arr" ? arrivalMinor(type, base, cfg) : new Set());

  return base.map((s, k) => {
    const cycleIdx = s.i != null ? s.i : s.cycle_index;
    const flagged = Array.isArray(s.flags) && s.flags.includes(type);
    const levels = {};

    if (flagged) {
      const lvl = s.flag_sev && s.flag_sev[type] ? sevToLevel(s.flag_sev[type])
        : evLevelByCycle[cycleIdx] || (type === "missed_arrival" ? "critical" : "warning");
      if (type === "pressure_deviation") {
        let hitSigs = s.flag_sig && Object.keys(s.flag_sig).length ? Object.keys(s.flag_sig) : null;
        if (!hitSigs) {
          hitSigs = sigs.filter(sg => evLevelBySig[`${cycleIdx}|${sg}`]);
          if (!hitSigs.length) {
            hitSigs = sigs.filter(sg => {
              const z = steps[sg].z[k], d = steps[sg].d[k];
              return z != null && z >= sigmaThr && Math.abs(d) >= psiThr;
            });
          }
          if (!hitSigs.length) hitSigs = ["csg"];
        }
        hitSigs.forEach(sg => {
          levels[sg] = s.flag_sig && s.flag_sig[sg] ? sevToLevel(s.flag_sig[sg]) : (evLevelBySig[`${cycleIdx}|${sg}`] || lvl);
        });
      } else {
        levels[sigs[0]] = lvl;
      }
    }

    // spikes visible in the data but below the backend alert threshold → yellow
    if (type === "pressure_deviation") {
      sigs.forEach(sg => {
        if (levels[sg]) return;
        const z = steps[sg].z[k], d = steps[sg].d[k];
        if (z != null && z >= sigmaThr * MINOR_SIGMA_FACTOR && Math.abs(d) >= psiThr * MINOR_PSI_FACTOR) levels[sg] = "minor";
      });
    }

    if (!flagged && trendMinorIdx.has(k) && !levels[sigs[0]]) levels[sigs[0]] = "minor";

    const out = {
      ...s,
      time: s.t ? String(s.t).slice(0,16) : undefined,
      _t: parseT(s.t),
      diff: s.tbg!=null&&s.line!=null ? +(s.tbg-s.line).toFixed(1) : null,
      anomaly_type: type,
      _actualFieldKey: meta.series || "arr",
      _levels: levels,
      _factors: (() => {
        const ev = data.events.find(x => x.well_id === active && x.type === type && x.cycle_index === cycleIdx);
        const nm = k => String(k).replace(/_/g, " ");
        const parts = [];
        if (ev && ev.evidence) {
          Object.entries(ev.evidence).forEach(([k, v]) => { if (v != null && typeof v !== "object") parts.push(`${nm(k)} = ${v}`); });
        }
        if (ev && ev.rca_candidates && ev.rca_candidates.length) {
          parts.push("likely causes: " + ev.rca_candidates.map(r => `${nm(r.cause)} (${Math.round((r.confidence || 0) * 100)}%)`).join(", "));
        }
        if (!parts.length && Object.keys(levels).length) {
          [["arr","arrival","min"],["csg","casing","psi"],["tbg","tubing","psi"],["line","line pressure","psi"]].forEach(([k, n, u]) => {
            if (s[k] != null && !isNaN(s[k])) parts.push(`${n} = ${s[k]} ${u}`);
          });
        }
        return parts.length ? parts.join("; ") : undefined;
      })(),
    };
    sigs.forEach(sg => MARKER_LEVELS.forEach(l => { out[`mk_${sg}_${l}`] = null; }));
    let status = "good", hit = null;
    Object.keys(levels).forEach(sg => {
      const v = s[sg] != null && !isNaN(s[sg]) ? s[sg] : null;
      out[`mk_${sg}_${levels[sg]}`] = v;
      if (v != null && hit == null) hit = v;
      status = maxLevel(status, levels[sg]);
    });
    out.status = status;
    out.hit = hit;
    return out;
  });
}

function AnomalyTypeView({type,data}) {
  const meta=ANOM_META[type];
  const events=data.events.filter(e=>e.type===type);
  const wellsWith=[...new Set(data.series.map(s=>s.well_id))];
  const [well,setWell]=useState(null);
  const active = well || (events[0]?.well_id) || wellsWith[0];
  const rows = buildTypedRows({type, meta, data, active});
  const hasT = hasStamps(rows);
  const L=(k,name,color,dash)=>({k,name,color,dash});
  const lines = meta.series==="arr" ? [L("arr","Arrival (min)",C.cyan)]
    : type==="pressure_deviation" ? [L("csg","Casing",C.cyan),L("tbg","Tubing",C.purple)]
    : type==="surface_restriction" ? [L("tbg","Tubing",C.purple),L("line","Line",C.muted),L("diff","TP − Line",C.orange,"4 2")]
    : [L("csg","Casing",C.cyan),L("line","Line",C.muted)];
  const evSev=s=>events.filter(e=>e.severity===s).length;
  const isMissed=type==="missed_arrival";
  const wellRisk=data.wells.find(w=>w.well_id===active);
  const markerSigs = MARKER_SIGNALS[type] || [meta.series || "arr"];
  const legendPayload = [
    ...lines.map(l=>({value:l.name,type:"line",color:l.color,id:l.k})),
    {value:"Critical",type:"circle",color:C.red,id:"lg_critical"},
    {value:"Warning",type:"circle",color:C.orange,id:"lg_warning"},
    {value:"Minor",type:"circle",color:SEV_YELLOW,id:"lg_minor"},
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <KpiRow items={[
        {label:`${data.labels[type]} Events`,value:events.length,color:C.cyan,tip:meta.kind==="trend"?"Trend-based detection":"Event-based detection"},
        {label:"Alarms",value:evSev("alarm"),color:C.red,tip:"Highest severity"},
        {label:"Warnings",value:evSev("warning"),color:C.amber,tip:"Needs review"},
        {label:"Wells Affected",value:new Set(events.map(e=>e.well_id)).size,color:C.orange,tip:"Wells with at least one event"},
      ]}/>

      {isMissed && (
        <Panel title="Alarm Rule" subtitle={`${data.missed_rule.count} missed arrivals within ${data.missed_rule.days} days → ALARM: something needs to change`}>
          <div style={{fontFamily:MONO,fontSize:11,color:data.wells_flagged.length?C.red:C.green}}>
            {data.wells_flagged.length ? `Flagged wells: ${data.wells_flagged.map(shortWell).join(", ")}` : "No well has hit the alarm rule."}
          </div>
        </Panel>
      )}

      <ChartCard title={meta.title} subtitle={`Well ${shortWell(active)} · last ${rows.length} cycles · markers = ${data.labels[type]}: red critical · orange warning · yellow minor`} explainer={meta.what} accent={C.cyan} height={260}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{top:28,right:20,left:10,bottom:20}}>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="i" tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} height={hasT?20:undefined}>
              {!hasT && <Label value="Cycle Number →" position="insideBottom" offset={-12} style={{fill:C.muted,fontSize:9,fontFamily:MONO}}/>}
            </XAxis>
            {dateXAxis(rows,"i",5,"Cycle Number →")}
            <YAxis tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} axisLine={false}>
              <Label value={meta.unit} angle={-90} position="insideLeft" style={{fill:C.muted,fontSize:9,fontFamily:MONO}}/>
            </YAxis>
            <Tooltip content={<SmartTooltip note="Hover for status and AI recommendation"/>}/>
            <Legend verticalAlign="top" height={28} payload={legendPayload} wrapperStyle={{fontFamily:MONO,fontSize:10,paddingBottom:4}}/>
            {lines.map(l=><Line key={l.k} type="monotone" dataKey={l.k} name={l.name} stroke={l.color} strokeWidth={1.6} strokeDasharray={l.dash} dot={false} connectNulls/>)}
            {[...MARKER_LEVELS].reverse().flatMap(lv => markerSigs.map(sg => (
              <Line key={`mk_${sg}_${lv}`} type="monotone" dataKey={`mk_${sg}_${lv}`} name={`${MARKER_SIG_NAME[sg]} · ${sevLevelLabel(lv)}`}
                    stroke="none" connectNulls={false} isAnimationActive={false} legendType="none"
                    dot={{r:lv==="critical"?6:lv==="warning"?5.5:4.5,fill:sevLevelColor(lv),stroke:C.card,strokeWidth:1.5}}
                    activeDot={{r:7,fill:sevLevelColor(lv),stroke:C.card,strokeWidth:1.5}}/>
            )))}
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {wellsWith.length>1 && (
        <div style={{display:"flex",gap:6,flexWrap:"wrap",fontFamily:MONO,fontSize:10,alignItems:"center"}}>
          <span style={{color:C.muted}}>Chart well:</span>
          {wellsWith.map(w=>(
            <button key={w} onClick={()=>setWell(w)} style={{fontFamily:MONO,fontSize:10,padding:"3px 9px",borderRadius:6,cursor:"pointer",
              border:`1px solid ${active===w?C.cyan+"66":C.border}`,background:active===w?`${C.cyan}14`:"transparent",color:active===w?C.cyan:C.muted}}>{shortWell(w)}</button>))}
        </div>
      )}

      {isMissed && (
        <Panel title="Arrival Health after a Missed Arrival" subtitle={`What the next arrival looked like, and how fluid recovery compared with this well's baseline. This feeds failure prediction.${data._windowed?" (These two bars cover ALL cycles, not just the window.)":""}`}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:28}}>
            <MiniBars title="Next arrival after a miss" counts={data.arrival_health.next_arrival_after_miss} order={["nominal","slow","fast","missed"]}
              colors={{nominal:C.green,slow:C.amber,fast:C.cyan,missed:C.red}}/>
            <MiniBars title="Fluid recovery on that next arrival" counts={data.arrival_health.fluid_recovery_after_miss} order={["below","normal","above"]}
              colors={{below:C.red,normal:C.green,above:C.cyan}}/>
          </div>
          <div style={{marginTop:18}}><RiskTable wells={enrichWellRisk(data.wells, data.series, data.events)}/></div>
        </Panel>
      )}

      <Panel title={`${data.labels[type]} Events`} subtitle={`Likely causes: ${meta.rca}. Click a row for evidence, root cause, prediction and recommended action.`}>
        <EventTable events={events}/>
      </Panel>
    </div>
  );
}

export default function AnomalyPage() {
  const { isDark } = useTheme();
  applyTheme(getColors(isDark));
  return (
    <div>
      <AnomalySection/>
    </div>
  );
}